import React from "react";
//#region types
import { GraphConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ChartContainerDataSet } from "models/propTypes/chartProps";
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import { ChartEnums, CurrencyEnums, IncomingInvoiceEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { ChartContainer, MultiLineChartTitle } from "components/common";
import { useIncomingInvoiceService } from "serviceHooks";
import ReportWidgetType = WintSuperkollAPIModel.ReportWidgetType;
//#endregion

const IncomingInvoiceTotalByStatusChart: React.FC = () => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const formatData = (chartData?: WintSuperkollAPIModel.ReportWidget): ChartContainerDataSet[] => {
    const data = chartData?.data;

    const result = EnumHelper.getNumericItemValues(IncomingInvoiceEnums.ViewState).map(
      (i): ChartContainerDataSet => ({
        label: incomingInvoiceService.getStateText(i as IncomingInvoiceEnums.ViewState),
        data:
          data
            ?.filter(
              (d) => incomingInvoiceService.toViewState(Number(d.name)) === (i as IncomingInvoiceEnums.ViewState)
            )
            ?.map((i) => (i.dataPoints ? i.dataPoints[0] : 0))
            .reduce((prev: number, current: number, index, arr: number[]) => prev + current, 0) ?? 0,
        isNotSelected: i === IncomingInvoiceEnums.ViewState.InBook || i === IncomingInvoiceEnums.ViewState.Unhandled,
      })
    );
    return result;
  };

  const getChartTitle = (chartData?: WintSuperkollAPIModel.ReportWidget) => {
    const data = chartData?.data;

    return (
      <MultiLineChartTitle
        rows={[
          {
            title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal),
            value: LocalizationHelperInstance.getFormattedCurrency(
              data
                ?.map((i) => (i.dataPoints ? i.dataPoints[0] : 0))
                .reduce((prev: number, current: number, index, arr: number[]) => prev + current, 0) ?? 0,
              CurrencyEnums.CurrencyCode.SEK
            ),
          },
        ]}
      />
    );
  };

  return (
    <ChartContainer
      useSkeleton={ReportWidgetType.Pie}
      chartAPIKey={GraphConstants.ChartKeys.IncomingInvoiceAmountByState}
      apiToChartHandler={formatData}
      filterToAPIHandler={incomingInvoiceService.reportToAPIFilter}
      chartTitle={getChartTitle}
      doughnutChartLegendType={ChartEnums.LegendType.WITH_VALUES}
      doughnutChartColorPalette={ChartEnums.DoughnutChartColorPalette.INCOMING_INVOICE_STATUS}
      usePageFilter={false}
      useClickableChart
      onDataPointClickedSetFilter={(label?: any) => {
        const clickedLabel = typeof label === "string" ? label : undefined;
        if (!clickedLabel) return {};

        const matchedState = EnumHelper.getNumericItemValues(IncomingInvoiceEnums.ViewState).find((i) => {
          const localized = incomingInvoiceService.getStateText(i as IncomingInvoiceEnums.ViewState);
          return localized === clickedLabel;
        }) as IncomingInvoiceEnums.ViewState | undefined;

        return matchedState !== undefined
          ? {
              state: [
                {
                  id: matchedState,
                  value: incomingInvoiceService.getStateText(matchedState),
                },
              ],
            }
          : {};
      }}
      onBackgroundClickedSetFilter={{ state: undefined }}
    />
  );
};

export default IncomingInvoiceTotalByStatusChart;
